import { buildApiUrl, secureFetch } from "@/lib/queryClient";
import { Session, SessionMessage, validateSession, validateSessionMessages, categorizeError, SessionError } from "@/types/session";

export interface CreateSessionResult {
  session: Session;
}

export interface ListSessionsResult {
  sessions: Session[];
}

export interface OpenSessionResult {
  session: Session;
  messages: SessionMessage[];
}

export type SessionOperationResult<T> = 
  | { ok: true; data: T }
  | { ok: false; error: SessionError };

async function create(title: string = "New Chat"): Promise<SessionOperationResult<CreateSessionResult>> {
  try {
    const response = await secureFetch(buildApiUrl("/api/chat/sessions"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
      },
      body: JSON.stringify({ title }),
    });

    if (!response.ok) {
      return { ok: false, error: categorizeError(null, response) };
    }

    const data = await response.json();
    const validation = validateSession(data);
    
    if (!validation.valid || !validation.session) {
      return {
        ok: false,
        error: {
          type: "validation",
          message: validation.error || "Invalid session data received",
          retryable: false,
        },
      };
    }

    return { ok: true, data: { session: validation.session } };
  } catch (error) {
    return { ok: false, error: categorizeError(error) };
  }
}

async function list(): Promise<SessionOperationResult<ListSessionsResult>> {
  try {
    const response = await secureFetch(buildApiUrl("/api/chat/sessions"), {
      credentials: "include",
      headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
    });

    if (!response.ok) {
      return { ok: false, error: categorizeError(null, response) };
    }

    const data = await response.json();
    const sessions: Session[] = [];

    if (Array.isArray(data)) {
      for (const item of data) {
        const validation = validateSession(item);
        if (validation.valid && validation.session) {
          sessions.push(validation.session);
        }
      }
    }

    return { ok: true, data: { sessions } };
  } catch (error) {
    return { ok: false, error: categorizeError(error) };
  }
}

async function open(sessionId: string): Promise<SessionOperationResult<OpenSessionResult>> {
  try {
    const sessionResponse = await secureFetch(buildApiUrl(`/api/chat/sessions/${sessionId}`), {
      credentials: "include",
      headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
    });

    if (!sessionResponse.ok) {
      return { ok: false, error: categorizeError(null, sessionResponse) };
    }

    const sessionData = await sessionResponse.json();
    const validation = validateSession(sessionData);

    if (!validation.valid || !validation.session) {
      return {
        ok: false,
        error: {
          type: "validation",
          message: validation.error || "Session data is corrupted",
          retryable: false,
        },
      };
    }

    const messagesResponse = await secureFetch(buildApiUrl(`/api/chat/sessions/${sessionId}/messages`), {
      credentials: "include",
      headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
    });

    if (!messagesResponse.ok) {
      return { ok: false, error: categorizeError(null, messagesResponse) };
    }

    const messagesData = await messagesResponse.json();
    const messagesValidation = validateSessionMessages(messagesData);

    return {
      ok: true,
      data: {
        session: validation.session,
        messages: messagesValidation.messages,
      },
    };
  } catch (error) {
    return { ok: false, error: categorizeError(error) };
  }
}

export const chatSession = {
  create,
  list,
  open,
};
